import { collection, doc } from "firebase/firestore";
import { type HttpsCallableResult, httpsCallable } from "firebase/functions";
import {
    deleteObject,
    getDownloadURL,
    type StorageReference,
    uploadBytes,
} from "firebase/storage";
import getIdToken from "./auth.util";
import type { Attachment } from "./common/types/Attachment";
import type DeveloperFixGoogleUrlOnCatalogItemsResponse from "./common/types/DeveloperFixGoogleUrlOnCatalogItemsResponse";
import type {
    AddInternalNoteResponse,
    AgentWriteInternalNotePayload,
} from "./common/types/Message";
import config from "./config";
import { db, functions } from "./Firebase";
import {
    conversationInternalNotesCollection,
    conversationMessagesCollection,
} from "./FirebaseBackend";
import { getImageSizeFromFile, validateSupportedFiletype } from "./utils/file.util";
import { fromTimestampToIsoDateString } from "./utils/timestamp.util";

export function messageDocument(conversationId: string, messageId: string) {
    return doc(
        collection(db, conversationMessagesCollection(conversationId)),
        messageId,
    );
}

export function internalNoteDocument(conversationId: string, messageId: string) {
    return doc(
        collection(db, conversationInternalNotesCollection(conversationId)),
        messageId,
    );
}

export async function writeInternalNote(
    payload: AgentWriteInternalNotePayload,
): Promise<HttpsCallableResult<AddInternalNoteResponse>> {
    const { token } = await getIdToken();
    const { note } = payload;
    const response = await fetch(
        `${config.apiServerBaseUrl}/lmt/conversations/${encodeURIComponent(payload.conversation)}/internal-notes`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                message: note.message,
                messageHtml: note.messageHtml ?? null,
                requestId: note.request ?? null,
                ...(note.attachments?.length ? { attachments: note.attachments } : {}),
                ...(note.articlesV2?.length
                    ? { catalogItemIds: note.articlesV2.map((item) => item.id) }
                    : {}),
                ...(note.bookingSuggestions?.length
                    ? { bookingIds: note.bookingSuggestions.map((b) => b.id) }
                    : {}),
                ...(payload.mentions?.length ? { mentions: payload.mentions } : {}),
                ...(payload.mentionDueDate
                    ? {
                          mentionDueDate: fromTimestampToIsoDateString(
                              payload.mentionDueDate,
                          ),
                      }
                    : {}),
            }),
        },
    );

    const json = (await response.json()) as {
        data?: { noteId: string };
        error?: string;
    };

    if (!response.ok) {
        return {
            data: {
                success: false,
                error: json.error ?? `Request failed: ${response.status}`,
            },
        };
    }

    return {
        data: {
            success: true,
            noteId: json.data!.noteId,
        },
    };
}

export async function uploadFile(
    file: File,
    fileRef: StorageReference,
    supportedFileTypes: string[] | undefined = undefined,
): Promise<Attachment> {
    try {
        if (supportedFileTypes)
            validateSupportedFiletype(file.type, supportedFileTypes);
        await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(fileRef);
        const size = file.type.startsWith("image")
            ? await getImageSizeFromFile(file)
            : { width: null, height: null };
        const attachment: Attachment = {
            id: crypto.randomUUID(),
            filename: file.name,
            storageFullPath: fileRef.fullPath,
            storageName: fileRef.name,
            url: downloadUrl,
            mimeType: file.type,
            width: size.width,
            height: size.height,
        };
        return await Promise.resolve(attachment);
    } catch (error) {
        return Promise.reject(error);
    }
}

export async function deleteFile(fileRef: StorageReference) {
    await deleteObject(fileRef);
}

export async function uploadImagesToBooking(bookingId: string, urls: string[]) {
    const { token } = await getIdToken();

    const response = await fetch(
        `${config.apiServerBaseUrl}/lmt/bookings/${encodeURIComponent(
            bookingId,
        )}/images`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ urls }),
        },
    );

    if (!response.ok) {
        throw new Error("Could not upload images to booking");
    }
}

export async function developerFixGoogleUrlOnCatalogItems() {
    return httpsCallable<never, DeveloperFixGoogleUrlOnCatalogItemsResponse>(
        functions,
        "developerFixGoogleUrlOnCatalogItems",
    )();
}
